/*
 * Behaviors for the sidebar block of `dev-article-content`:
 *   1. Build the table of contents from the article body's headings.
 *   2. Track active TOC item via IntersectionObserver.
 *   3. Compute reading progress and fill the progress bar.
 *   4. Wire share links (LinkedIn / X / Email) + copy-link button.
 *
 * Visibility/positioning is handled in CSS via `position: sticky` — no
 * JS visibility toggle here; if the sidebar block isn't present, this
 * file does nothing.
 */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.devSidebarInit === '1') return;
    root.dataset.devSidebarInit = '1';

    // The body lives in the same section as the sidebar — scope the lookup
    // to that section instance so multiple article-content sections on the
    // same page don't cross-talk.
    var section = root.closest('.dev-article-content');
    if (!section) return;
    var content = section.querySelector('[data-dev-article-body]');
    if (!content) return;

    var headingSelector = root.dataset.headingSelector || 'h2, h3';

    buildToc(root, content, headingSelector);
    setupProgress(root, content);
    setupShare(root);
  }

  /* Table of contents
     ------------------------------------------------------------- */
  function buildToc(root, content, headingSelector) {
    var tocList = root.querySelector('[data-toc-list]');
    var tocWrap = root.querySelector('[data-toc]');
    if (!tocList || !tocWrap) return;

    var headings = Array.prototype.slice.call(
      content.querySelectorAll(headingSelector)
    );
    if (!headings.length) {
      tocWrap.hidden = true;
      return;
    }

    var slugSeen = {};
    headings.forEach(function (heading, idx) {
      // Ensure each heading has a stable id for the anchor link. Merchant-set
      // ids in the article HTML are honored; missing ones get slugified.
      if (!heading.id) {
        var base = slugify(heading.textContent) || 'section-' + (idx + 1);
        var slug = base;
        var counter = 2;
        while (slugSeen[slug] || document.getElementById(slug)) {
          slug = base + '-' + counter++;
        }
        slugSeen[slug] = true;
        heading.id = slug;
      }

      var li = document.createElement('li');
      li.className =
        'dev-article-content__toc-item dev-article-content__toc-item--' +
        heading.tagName.toLowerCase();
      li.dataset.targetId = heading.id;

      var a = document.createElement('a');
      a.className = 'dev-article-content__toc-link';
      a.href = '#' + heading.id;
      a.textContent = heading.textContent.trim();
      a.addEventListener('click', function (e) {
        e.preventDefault();
        var target = document.getElementById(heading.id);
        if (!target) return;
        // Lock the active state to the clicked heading and suppress
        // observer-driven updates until the smooth scroll settles —
        // otherwise headings the page passes through can win the
        // activation race and a neighbor ends up highlighted.
        lockActive(heading.id);
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        history.replaceState(null, '', '#' + heading.id);
      });
      li.appendChild(a);
      tocList.appendChild(li);
    });

    tocWrap.hidden = false;

    // Active-section tracking. rootMargin pulls the activation line ~25%
    // down the viewport so the highlight matches what the reader is looking at.
    if (!('IntersectionObserver' in window)) return;

    var lockedId = null;
    var lockedUntil = 0;
    var unlockTimer = null;

    function setActive(id) {
      var li = tocList.querySelector('[data-target-id="' + id + '"]');
      if (!li || li.classList.contains('is-active')) return;
      tocList.querySelectorAll('.is-active').forEach(function (el) {
        el.classList.remove('is-active');
      });
      li.classList.add('is-active');
    }

    function lockActive(id) {
      lockedId = id;
      // 1.2s ceiling — long enough for smooth scroll on long pages, short
      // enough that the lock releases on its own if scrollend never fires
      // (older browsers, or if the user interrupts mid-scroll).
      lockedUntil = Date.now() + 1200;
      setActive(id);

      if (unlockTimer) clearTimeout(unlockTimer);
      unlockTimer = setTimeout(releaseLock, 1200);

      if ('onscrollend' in window) {
        window.addEventListener('scrollend', onScrollEnd, { once: true });
      }
    }

    function onScrollEnd() {
      // Grace period so a stray observer callback that fires the instant
      // the scroll ends still lands on the clicked target.
      setTimeout(releaseLock, 80);
    }

    function releaseLock() {
      lockedId = null;
      lockedUntil = 0;
      if (unlockTimer) {
        clearTimeout(unlockTimer);
        unlockTimer = null;
      }
    }

    var observer = new IntersectionObserver(
      function (entries) {
        if (lockedId && Date.now() < lockedUntil) {
          // Re-assert in case a transient observer call removed it.
          setActive(lockedId);
          return;
        }

        // Pick the topmost heading currently inside the activation band.
        // Multiple headings can intersect in the same callback batch (two
        // short adjacent sections, scrolling fast); the last entry in the
        // array isn't necessarily the right one. Reading boundingClientRect
        // here is fine — it's already up to date inside the observer cb.
        var bestId = null;
        var bestTop = Infinity;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var top = entry.boundingClientRect.top;
          if (top < bestTop) {
            bestTop = top;
            bestId = entry.target.id;
          }
        });

        if (bestId) setActive(bestId);
      },
      {
        rootMargin: '-25% 0px -65% 0px',
        threshold: 0,
      }
    );

    headings.forEach(function (heading) {
      observer.observe(heading);
    });
  }

  /* Reading progress
     ------------------------------------------------------------- */
  function setupProgress(root, content) {
    var bar = root.querySelector('[data-progress-bar]');
    var pct = root.querySelector('[data-progress-percent]');
    var track = root.querySelector('[data-progress-track]');
    if (!bar) return;

    var ticking = false;

    function compute() {
      ticking = false;
      var rect = content.getBoundingClientRect();
      var winH = window.innerHeight;
      // 0% when the article top hits the viewport bottom, 100% when the
      // article bottom hits the viewport bottom.
      var scrolled = winH - rect.top;
      var total = rect.height;
      var raw = total > 0 ? scrolled / total : 0;
      var clamped = Math.max(0, Math.min(1, raw));
      var percent = Math.round(clamped * 100);
      bar.style.width = percent + '%';
      if (pct) pct.textContent = percent + '%';
      if (track) track.setAttribute('aria-valuenow', String(percent));
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(compute);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    compute();
  }

  /* Share buttons
     ------------------------------------------------------------- */
  function setupShare(root) {
    var url = window.location.href;
    var title = root.dataset.shareTitle || document.title;
    var encodedUrl = encodeURIComponent(url);
    var encodedTitle = encodeURIComponent(title);

    var endpoints = {
      linkedin:
        'https://www.linkedin.com/sharing/share-offsite/?url=' + encodedUrl,
      twitter:
        'https://twitter.com/intent/tweet?url=' +
        encodedUrl +
        '&text=' +
        encodedTitle,
      email: 'mailto:?subject=' + encodedTitle + '&body=' + encodedUrl,
    };

    root.querySelectorAll('[data-share]').forEach(function (btn) {
      var kind = btn.dataset.share;
      if (kind === 'copy') {
        btn.addEventListener('click', function () {
          copyLink(root, btn, url);
        });
      } else if (endpoints[kind]) {
        btn.setAttribute('href', endpoints[kind]);
      }
    });
  }

  function copyLink(root, btn, url) {
    var done = function (ok) {
      var defaultIcon = btn.querySelector('[data-copy-default]');
      var successIcon = btn.querySelector('[data-copy-success]');
      var feedback = root.querySelector('[data-share-feedback]');
      var copiedLabel = root.dataset.copiedLabel || 'Link copied!';
      if (ok) {
        btn.classList.add('is-copied');
        if (defaultIcon) defaultIcon.hidden = true;
        if (successIcon) successIcon.hidden = false;
        if (feedback) {
          feedback.textContent = copiedLabel;
          feedback.classList.add('is-visible');
        }
        // Transient toast — reset state after a beat.
        setTimeout(function () {
          btn.classList.remove('is-copied');
          if (defaultIcon) defaultIcon.hidden = false;
          if (successIcon) successIcon.hidden = true;
          if (feedback) {
            feedback.classList.remove('is-visible');
            feedback.textContent = '';
          }
        }, 2200);
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(
        function () {
          done(true);
        },
        function () {
          fallbackCopy(url, done);
        }
      );
    } else {
      fallbackCopy(url, done);
    }
  }

  // execCommand fallback for older browsers / non-HTTPS dev contexts.
  function fallbackCopy(url, done) {
    var ta = document.createElement('textarea');
    ta.value = url;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      var ok = document.execCommand('copy');
      done(ok);
    } catch (err) {
      done(false);
    }
    document.body.removeChild(ta);
  }

  function slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function boot() {
    document
      .querySelectorAll('[data-dev-article-sidebar]')
      .forEach(function (el) {
        init(el);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when Shopify re-renders the section in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-dev-article-sidebar]');
    if (el) init(el);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target.querySelector
      ? e.target.querySelector('[data-dev-article-sidebar]')
      : null;
    if (el) init(el);
  });
})();
